'use client';

import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { analytics } from '@kit/analytics';
import { getCountryFieldOptions } from '@kit/artists';
import { DynamicFormRenderer } from '@kit/auditions/components';
import {
  createApplicationAction,
  submitApplicationAction,
} from '@kit/auditions/server';
import type { ApplicationStructure } from '@kit/auditions/types';
import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Form } from '@kit/ui/form';
import { ScrollArea } from '@kit/ui/scroll-area';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { BrandLink } from '~/components/brand-link';
import { DancerEvent } from '~/lib/analytics/dancer-events';
import { applicationFileUploadAdapter } from '../../app/home/(user)/artist/profile/setup/_lib/media-picker-adapters';
import { deleteDancerMediaAction } from '../../app/home/(user)/artist/profile/setup/_lib/server/media-actions';

// Country-backed choice fields store an ISO code but structures don't inline
// the country list; same single source as the apply/edit surfaces (Rule #8).
const COUNTRY_OPTIONS = getCountryFieldOptions();
const APPLICATION_DYNAMIC_OPTIONS = {
  residenceCountry: COUNTRY_OPTIONS,
  nationalities: COUNTRY_OPTIONS,
};

interface ApplyDialogProps {
  auditionId: string;
  auditionTitle: string;
  applicationStructure: ApplicationStructure | null;
  /**
   * Default values computed server-side from the dancer's profile via the
   * PROFILE_FIELD_MAP registry. When absent, the form opens empty. See
   * `packages/features/auditions/src/lib/profile-field-mapping.ts`.
   */
  prefillDefaults?: Record<string, unknown>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Renders the audition application form (or a confirmation if no custom form
 * has been configured). On submit it creates the application record with the
 * filled-in fields and immediately submits it (status: open → submitted).
 */
export function ApplyDialog({
  auditionId,
  auditionTitle,
  applicationStructure,
  prefillDefaults,
  open,
  onOpenChange,
}: ApplyDialogProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const hasCustomForm =
    !!applicationStructure &&
    Array.isArray(applicationStructure.fields) &&
    applicationStructure.fields.filter(
      (f) => f.type !== 'heading' && f.type !== 'paragraph',
    ).length > 0;

  const form = useForm<Record<string, unknown>>({
    defaultValues: prefillDefaults ?? {},
  });

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setIsSubmitting(true);
      try {
        const createResult = await createApplicationAction({
          auditionId,
          fields: hasCustomForm ? values : {},
        });

        if (!createResult.success) {
          const msg =
            typeof createResult.error === 'string'
              ? createResult.error
              : 'Failed to create application';
          toast.error(msg);
          return;
        }

        const submitResult = await submitApplicationAction(
          createResult.data.id,
        );

        if (!submitResult.success) {
          toast.warning(t('auditions:apply.toastDraftSaved'));
          setSubmitted(true);
          router.refresh();
          return;
        }

        // Surface profile backfill count to the dancer so they know which
        // form values landed on their profile (registry-driven, never blocks).
        const backfillCount =
          'backfillCount' in createResult &&
          typeof createResult.backfillCount === 'number'
            ? createResult.backfillCount
            : 0;
        if (backfillCount > 0) {
          toast.success(
            backfillCount === 1
              ? 'Application sent — 1 detail added to your profile.'
              : `Application sent — ${backfillCount} details added to your profile.`,
          );
        }

        // "Finding" activation signal — a fully-submitted audition application
        // (H1/H2 candidate). Draft-only saves (line ~109) are intentionally
        // not counted here.
        void analytics.trackEvent(DancerEvent.AuditionApplied, {
          audition_id: auditionId,
        });

        setSubmitted(true);
        router.refresh();
      } catch {
        toast.error(t('auditions:apply.toastUnexpectedError'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [auditionId, hasCustomForm, router, t],
  );

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onOpenChange(false);
      setTimeout(() => {
        setSubmitted(false);
        form.reset();
      }, 300);
    }
  }, [isSubmitting, onOpenChange, form]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[640px]">
        {submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <Trans
                  i18nKey="auditions:apply.successTitle"
                  defaults="Application Submitted!"
                />
              </DialogTitle>
              <DialogDescription>
                <Trans
                  i18nKey="auditions:apply.successDescription"
                  defaults="Your application has been submitted successfully. You can track its status in My Applications."
                />
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                <Trans i18nKey="common:close" defaults="Close" />
              </Button>
              <Button asChild>
                <BrandLink to="/home/auditions/my-applications">
                  <Trans
                    i18nKey="auditions:apply.viewApplications"
                    defaults="View My Applications"
                  />
                </BrandLink>
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                <Trans
                  i18nKey="auditions:apply.title"
                  defaults="Apply to Audition"
                />
              </DialogTitle>
              <DialogDescription>{auditionTitle}</DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)}>
                {hasCustomForm ? (
                  <ScrollArea className="max-h-[60vh] pr-4">
                    <div className="py-2">
                      <DynamicFormRenderer
                        structure={applicationStructure!}
                        form={form}
                        uploadFile={applicationFileUploadAdapter}
                        deleteFile={deleteDancerMediaAction}
                        dynamicOptions={APPLICATION_DYNAMIC_OPTIONS}
                      />
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-muted-foreground py-4 text-sm">
                    <Trans
                      i18nKey="auditions:apply.confirmDescription"
                      defaults="Click Submit to send your application. The organization will review it and get back to you."
                    />
                  </div>
                )}

                <DialogFooter className="mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <Trans i18nKey="common:cancel" defaults="Cancel" />
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        <Trans
                          i18nKey="auditions:submitting"
                          defaults="Submitting..."
                        />
                      </>
                    ) : (
                      <Trans
                        i18nKey="auditions:submitApplication"
                        defaults="Submit Application"
                      />
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
